import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

/**
 * Custom hook for fetching and managing user roles
 * 
 * Phase 1: Consolidated role system
 * - Active roles: super_admin, admin
 * - Deprecated (Phase 1): template_admin, ats_admin, business_admin
 * 
 * Supports multiple roles per user and provides helper functions
 * to check for specific roles or any of a list of roles.
 */
export function useUserRoles(userId?: string) {
  const [roles, setRoles] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    async function fetchRoles() {
      try {
        setLoading(true);
        const { data: { user } } = await supabase.auth.getUser();
        const targetUserId = userId || user?.id;

        if (!targetUserId) {
          setRoles([]);
          return;
        }

        const { data, error: rolesError } = await supabase
          .from('user_roles')
          .select('role_id')
          .eq('user_id', targetUserId);

        if (rolesError) throw rolesError;

        setRoles(data?.map(r => r.role_id) || []);
        setError(null);
      } catch (err) {
        console.error('Error fetching user roles:', err);
        setError(err as Error);
        setRoles([]);
        
        // Log security event for suspicious activity (fire and forget)
        if (err instanceof Error && err.message.includes('JWT')) {
          (async () => {
            try {
              await supabase.rpc('log_security_event', {
                p_event_type: 'role_fetch_jwt_error',
                p_severity: 'high',
                p_resource_type: 'user_roles',
                p_details: { error: err.message }
              });
            } catch (logErr) {
              console.error('Failed to log security event:', logErr);
            }
          })();
        }
      } finally {
        setLoading(false);
      }
    }

    fetchRoles();
  }, [userId]);

  const hasRole = (role: string) => roles.includes(role);
  const hasAnyRole = (checkRoles: string[]) => checkRoles.some(r => roles.includes(r));

  // Legacy support - keeping helpers but they use deprecated role names
  // Prefer using useRBAC() hook for new code
  return {
    roles,
    loading,
    error,
    hasRole,
    hasAnyRole,
    isAdmin: hasRole('admin') || hasRole('super_admin'),
    isSuperAdmin: hasRole('super_admin'),
    // Deprecated: Use useRBAC().can('manage', 'templates') instead
    canManageTemplates: hasAnyRole(['super_admin', 'template_admin', 'admin']),
    // Deprecated: Use useRBAC().can('manage', 'businesses') instead
    canManageBusiness: hasAnyRole(['super_admin', 'business_admin', 'admin']),
  };
}
